import { useState } from 'react';
import { useSnackbar } from 'notistack';

import {
  PdvDialog,
  PdvDialogBanner,
  PdvDialogCard,
  PdvDialogField,
  PdvDialogLabel,
  PdvPositiveBadgeIcon,
  PdvSentBadgeIcon,
  PdvTextarea,
  PdvWarningBadgeIcon,
  PillButton,
  PillTabs
} from 'componentsV2/pdv';
import { useSubscribedMutation } from 'Hooks/React-Query';
import { raisePdvFlagMutation } from 'Services/Mutations/Pdv';
import {
  pdvKeys,
  type PdvFlag,
  type PdvFlagIssue,
  type PdvProductFeedbackContext
} from 'Services/Query/Pdv';
import { pdvDate, pdvErrorMessage } from 'utilsV2/pdv';

import { AttributePicker, type AttributeOption } from './attribute-picker';
import { useStyles } from './styles';
import { missingStep, sentLine } from './utils';

const MODES: { value: PdvFlagIssue; label: string }[] = [
  { value: 'wrong', label: 'Wrong value' },
  { value: 'missing', label: 'Missing' },
  { value: 'other', label: 'Something else' }
];

const STATUS_WORD: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In progress'
};

interface Props {
  sku: string;
  desc?: string;
  /** undefined when the product isn't in a scored class: free text only */
  product?: PdvProductFeedbackContext;
  openFlags: PdvFlag[];
  me: string;
  canSend: boolean;
  onClose: () => void;
}

/**
 * Report a data issue, from the Catalog product details popup.
 *
 * Nothing here writes to a system of record. Reports land in Admin › Rep Feedback,
 * where the product team triages them and fixes the data in the PIM. A second
 * report by the same rep on the same attribute merges into the first one.
 */
export const ReportDialog = ({
  sku,
  desc,
  product,
  openFlags,
  me,
  canSend,
  onClose
}: Props) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  // Refreshes the product's flags here and the admin queue.
  const { mutateAsync: raiseFlag } = useSubscribedMutation(
    raisePdvFlagMutation(),
    [pdvKeys.all[0]]
  );

  const present: AttributeOption[] = (product?.present ?? []).map(name => ({
    name,
    value: product?.values?.[name]
  }));
  const missing: AttributeOption[] = (product?.missing ?? []).map(name => ({
    name
  }));
  let initialMode: PdvFlagIssue = 'other';
  if (product) initialMode = present.length ? 'wrong' : 'missing';

  const [mode, setMode] = useState<PdvFlagIssue>(initialMode);
  const [attr, setAttr] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentLog, setSentLog] = useState<string[]>([]);

  const options = mode === 'wrong' ? present : missing;
  const current = attr ? (options.find(o => o.name === attr)?.value ?? '') : '';
  const blocker = missingStep(mode, attr, note);

  const flagOn = (name: string) => openFlags.find(f => f.attr === name);
  const mineOn = (name: string) =>
    openFlags.find(f => f.attr === name && f.createdBy === me);

  const reset = () => {
    setMode(initialMode);
    setAttr(null);
    setMenuOpen(false);
    setNote('');
    setSent(false);
  };

  /** A different kind of problem means a different attribute list. */
  const switchMode = (next: PdvFlagIssue) => {
    if (next === mode) return;
    setMode(next);
    setAttr(null);
    setMenuOpen(false);
  };

  const submit = async () => {
    if (blocker || busy) return;
    setBusy(true);
    try {
      await raiseFlag({
        sku,
        issue: mode,
        ...(mode !== 'other' && attr ? { attr } : {}),
        ...(note.trim() ? { note: note.trim() } : {})
      });
      setSentLog(prev => [...prev, sentLine(mode, attr, current, note)]);
      setSent(true);
    } catch (e) {
      enqueueSnackbar(pdvErrorMessage(e, 'Could not send the report.'), {
        variant: 'error'
      });
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <PdvDialog
        surface="grey"
        onClose={onClose}
        icon={<PdvSentBadgeIcon />}
        title="Report sent"
        subtitle="The product team will review it."
        actions={
          <>
            <PillButton variant="secondary" onClick={reset}>
              Report another issue
            </PillButton>
            <PillButton
              variant="brand"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              onClick={onClose}>
              Done
            </PillButton>
          </>
        }>
        {sentLog.map((line, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <PdvDialogBanner key={`${line}-${i}`}>{line}</PdvDialogBanner>
        ))}
      </PdvDialog>
    );
  }

  const chosenFlag = attr && !menuOpen ? flagOn(attr) : undefined;
  const chosenMine = attr && !menuOpen ? mineOn(attr) : undefined;

  return (
    <PdvDialog
      surface="grey"
      onClose={onClose}
      title="Report a data issue"
      subtitle={
        <>
          {desc ? `${desc} · ` : ''}SKU {sku}
          {sentLog.length > 0 && (
            <span className={classes.sentCount}> · {sentLog.length} sent</span>
          )}
        </>
      }
      actions={
        <>
          <PillButton variant="tertiary" onClick={onClose}>
            Cancel
          </PillButton>
          <PillButton
            variant="brand"
            disabled={Boolean(blocker) || busy || !canSend}
            title={blocker || undefined}
            onClick={submit}>
            Send report
          </PillButton>
        </>
      }>
      <PdvDialogCard>
        <PdvDialogLabel>What&apos;s the problem?</PdvDialogLabel>
        <PillTabs
          semantics="radio"
          ariaLabel="What's the problem?"
          className={classes.segment}
          value={mode}
          onChange={switchMode}
          items={MODES.map(m => ({
            ...m,
            disabled: !product && m.value !== 'other'
          }))}
        />

        {!product && (
          <PdvDialogBanner>
            This product isn&apos;t in a scored class yet, so there are no
            attributes to pick from. Describe the problem and it still reaches
            the product team.
          </PdvDialogBanner>
        )}

        {mode !== 'other' && (
          <>
            <PdvDialogField label="Which attribute?">
              <AttributePicker
                options={options}
                value={attr}
                showValue={mode === 'wrong'}
                open={menuOpen}
                onOpenChange={setMenuOpen}
                onPick={name => {
                  setAttr(name);
                  setMenuOpen(false);
                }}
                badgeFor={name => {
                  if (mineOn(name)) return 'mine';
                  return flagOn(name) ? 'other' : null;
                }}
                emptyText={
                  mode === 'wrong'
                    ? 'This product has no values yet. Choose Missing instead.'
                    : 'Nothing is missing on this product.'
                }
              />
            </PdvDialogField>

            {chosenMine && (
              <PdvDialogBanner tone="positive" icon={<PdvPositiveBadgeIcon />}>
                You already reported this. Sending again adds to the same
                report.
              </PdvDialogBanner>
            )}
            {!chosenMine && chosenFlag && (
              <PdvDialogBanner tone="warning" icon={<PdvWarningBadgeIcon />}>
                Already reported on {pdvDate(chosenFlag.createdAt)} ·{' '}
                {STATUS_WORD[chosenFlag.status] ?? 'Open'}. You can still add
                details.
              </PdvDialogBanner>
            )}

            <PdvDialogField label="Add details (optional)">
              <PdvTextarea
                placeholder="Anything that helps the product team fix it, like the correct value"
                value={note}
                onChange={e => setNote(e.target.value)}
              />
            </PdvDialogField>
          </>
        )}

        {mode === 'other' && (
          <PdvDialogField label="Describe the problem">
            <PdvTextarea
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              placeholder="What did you notice?"
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </PdvDialogField>
        )}
      </PdvDialogCard>
    </PdvDialog>
  );
};
